<template>
    <el-container style="height: calc(100vh - 52px)">
        <!-- 搜索条件区域 -->
        <el-header style="height: auto;" class="bg-gray-200 p-3 md:p-5">
            <InventorySearchForm 
                :model-value="searchForm" 
                :login-user-role="loginUserRole || ''"
                :login-user-role-is-head="loginUserRoleIsHead"
                @update:model-value="handleSearchFormUpdate"
                @organization-id-change="searchForm.organization_id = $event"
                @search="handleSearch"
                @add="handleAdd"
                @export="handleExport"
                @import="handleImport"
            />
        </el-header>

        <!-- 数据展示区域 -->
        <el-main style="padding: 0px; padding-bottom: 20px;">
            <div class="md:hidden custom-title flex flex-col">
                <span>在庫一覧（在庫数　アイテム名　有効期間）</span>
                <div class="ml-auto">
                    <div class="flex items-center space-x-1">
                        <span class="text-white">ソート:</span>
                        <el-button 
                            size="small" 
                            @click="handleSort2('material_name')"
                            :class="['mobile-sort-btn', sortField === 'material_name' ? 'active' : '']">
                            アイテム名    
                        </el-button>
                        <el-button 
                            size="small" 
                            @click="handleSort2('quantity')"
                            :class="['mobile-sort-btn', sortField === 'quantity' ? 'active' : '']">
                            在庫数    
                        </el-button>
                        <span>アイテム数: {{ totalRecords }}</span>
                    </div>
                </div>
            </div>

            <!-- 系统管理员，（非明确部门）本部管理员 Level 1: 组织维度商品合计一览表 -->
            <OrganizationLevelInventoryList
                :key="`org-${tableKey}`"
                ref="organizationTableRef"
                v-if="currentLevel === 1"
                :data="tableData"
                :loading="loading"
                :login-user-role="loginUserRole || ''"
                :login-user-role-is-head="loginUserRoleIsHead"
                :current-page="currentPage"
                :page-size="pageSize"
                :total-records="totalRecords"
                @edit="handleEditInventory"
                @delete="handleDeleteInventory"
                @alarm="handleAlarmInventory"
                @constant="handleConstantInventory"
                @expand="handleExpand"
                @nested-expand="handleNestedExpand"
                @collapse="handleCollapse"
                @sort="handleSort"
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange" 
                @transfer-inventory="handleTransferInventory"
                @quantity-update="handleQuantityUpdate"
            />

            <!-- 明确部门的系统管理员与本部管理员，非本部管理员，Level 2: 部门维度商品合计一览表 -->
            <DepartmentLevelInventoryList
                :key="`dept-${tableKey}`"
                ref="departmentTableRef"
                v-else-if="currentLevel === 2"
                :data="tableData"
                :loading="loading"
                :login-user-role="loginUserRole || ''"
                :login-user-role-is-head="loginUserRoleIsHead"
                :search-department-id="searchDepartmentId || undefined"
                :is-standalone="true"
                :current-page="currentPage"
                :page-size="pageSize"
                :total-records="totalRecords"
                @edit="handleEditInventory"
                @delete="handleDeleteInventory"
                @alarm="handleAlarmInventory"
                @constant="handleConstantInventory"
                @expand="handleExpand"
                @collapse="handleCollapse"
                @quantity-update="handleQuantityUpdate"
                @sort="handleSort"
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                @transfer-inventory="handleTransferInventory"
            />

            <!-- 一般用户 Level 3: 详细级别库存信息（直接从 Level 1 查询返回） -->
            <div v-else-if="currentLevel === 3" class="w-full">
                <DetailLevelInventoryList
                    :key="`detail-${tableKey}`"
                    ref="detailTableRef"
                    :data="tableData"
                    :loading="loading"
                    :is-standalone="true"
                    :current-page="currentPage"
                    :page-size="pageSize"
                    :total-records="totalRecords"
                    @edit="handleEditInventory"
                    @delete="handleDeleteInventory"
                    @alarm="handleAlarmInventory"
                    @quantity-update="handleQuantityUpdate"
                    @sort="handleSort"
                    @size-change="handleSizeChange"
                    @current-change="handleCurrentChange"
                    @transfer-inventory="handleTransferInventory"
                />
            </div>

            <!-- 分页组件 -->
            <div class="flex w-full justify-end mt-4">
                <div class="hidden md:block mr-3">
                    <el-pagination 
                        v-model:current-page="currentPage" 
                        v-model:page-size="pageSize"
                        :page-sizes="[10, 20, 50, 100]" 
                        layout="total, sizes, prev, pager, next, jumper"
                        :total="totalRecords" 
                        @size-change="handleSizeChange" 
                        @current-change="handleCurrentChange" 
                    />
                </div>

                <div class="md:hidden">
                    <el-pagination 
                        layout="prev, pager, next" 
                        :total="totalRecords"
                        @current-change="handleCurrentChange" 
                    />
                </div>
            </div>
        </el-main>
    </el-container>

    <!-- 对话框组件 -->
    <InventoryDialogs
        v-model:form-visible="dialogFormVisible"
        v-model:alarm-visible="dialogAlarmVisible"
        v-model:constant-visible="dialogConstantVisible"
        v-model:image-preview-visible="dialogVisible"
        v-model:transfer-inventory-visible="dialogTransferInventoryVisible"
        v-model:transfer-inventory-step="transferInventoryStep"
        :form="form"
        :alarm-form="alarmForm"
        :constant-form="constantForm"
        :transfer-inventory-form="transferInventoryForm"
        :target-inventory-list="targetInventoryList"
        :target-inventory-list-loading="targetInventoryListLoading"
        :selected-target-inventory-id="selectedTargetInventoryId"
        :dialog-image-url="dialogImageUrl"
        :file-list="fileList"
        :form-show-del="dialogFormShowDel"
        :login-user-role="loginUserRole || ''"
        :login-user-role-is-head="loginUserRoleIsHead"
        :errors="errors"
        :constant-setting-level="constantSettingLevel"
        @update:form="handleFormUpdate"     
        @update:alarm-form="handleAlarmFormUpdate"
        @update:constant-form="handleConstantFormUpdate"
        @update:transfer-inventory-form="handleTransferInventoryFormUpdate"
        @submit="handleFormSubmit"
        @alarm-submit="handleAlarmSubmit"
        @constant-submit="handleConstantSubmit"
        @transfer-inventory-submit="handleTransferInventorySubmit"
        @transfer-inventory-request-target-inventory-list="getTargetInventoryList"
        @target-inventory-select="handleTargetInventorySelect"
        @delete-reminder="handleDeleteReminder"
        @image-change="handleImageChange"
        @image-remove="handleImageRemove"
        @image-preview="handleImagePreview"
        @scan-start="handleScanStart"
        @jancode-search="handleJancodeSearch"
        @create-new-record="handleCreateNewRecord"
        @update:errors="handleErrorsUpdate"
        @organization-id-change="form.organization_id = $event"
    />
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, nextTick } from 'vue';
import { ElMessage, ElMessageBox, UploadFile } from 'element-plus';
import axios, { AxiosResponse } from 'axios';
import router from '@/router';
import { ROLES, ROUTES, ALL, IMG_MAX_SIZE } from '@/config/constants';
import { ErrorCode, ResponseData, ResponseStatus, ReminderSettingResponseData, UploadImageResponseData } from '@/components/common/response';
import { getErrorMessage, PageType } from '@/components/common/errorMessage';
import { Operation, SearchRequest } from '@/components/common/request';

// 导入子组件
import InventorySearchForm from './InventorySearchForm.vue';
import OrganizationLevelInventoryList from './OrganizationLevelInventoryList.vue';
import DepartmentLevelInventoryList from './DepartmentLevelInventoryList.vue';
import DetailLevelInventoryList from './DetailLevelInventoryList.vue';
import InventoryDialogs from './InventoryDialogs.vue';
import { AlarmInfo, ConstantInfo, InventoryInfo } from './type';
import { resolveExpirationDateFromDay } from '@/components/common/StringUtils';

export default defineComponent({
    name: 'InventoryLevelManager',
    components: {
        InventorySearchForm,
        OrganizationLevelInventoryList,
        DepartmentLevelInventoryList,
        DetailLevelInventoryList,
        InventoryDialogs,
    },
    setup() {
        const loginUserRole = localStorage.getItem('role');
        const loginUserRoleIsHead = localStorage.getItem('is_head') === '1';
        const loginUserOrganizationId = localStorage.getItem('organization_id');
        const loginUserOrganizationName = localStorage.getItem('organization_name');
        const loginUserDepartmentId = localStorage.getItem('department_id');
        const loginUserDepartmentName = localStorage.getItem('department_name');

        // 响应式数据
        const loading = ref<boolean>(true);
        // const isGrid = ref<boolean>(false);
        const currentLevel = ref<number>(1); // 当前显示的层级
        const tableData = ref<InventoryInfo[]>([]);          // 排序完成，显示在表格里的数据（单页的数据）
        const allData = ref<InventoryInfo[]>([]);            // 存储所有查询结果
        const totalRecords = ref<number>(0);
        const currentPage = ref<number>(1);
        const pageSize = ref<number>(10);
        const searchDepartmentId = ref<string | null>(null);

        // 排序相关状态
        const sortField = ref<string>('material_name'); // 当前排序字段
        const sortOrder = ref<'asc' | 'desc'>('asc'); // 当前排序方向

        // 排序配置 - 方便添加新的排序字段
        const sortConfig = {
            'material_name': {
                label: 'アイテム名',
                mobileLabel: 'アイテム名',
                type: 'string' as const,
                defaultOrder: 'asc' as const
            },
            'quantity': {
                label: '在庫数',
                mobileLabel: '在庫数',
                type: 'number' as const,
                defaultOrder: 'desc' as const
            },
            'organization_name': {
                label: '組織',
                mobileLabel: '組織',
                type: 'string' as const,
                defaultOrder: 'asc' as const
            },
            'department_name': {
                label: '部署',
                mobileLabel: '部署',
                type: 'string' as const,
                defaultOrder: 'asc' as const
            },
            'category_name': {
                label: 'カテゴリ',
                mobileLabel: 'カテゴリ',
                type: 'string' as const,
                defaultOrder: 'asc' as const
            },
            'storage_date': {
                label: '保管日',
                mobileLabel: '保管日',
                type: 'date' as const,
                defaultOrder: 'desc' as const
            },
            'expiration_date': {
                label: '有効期限',
                mobileLabel: '有効期限',
                type: 'date' as const,
                defaultOrder: 'asc' as const
            }
        };

        // 表格ref
        const organizationTableRef = ref();
        const departmentTableRef = ref();
        const detailTableRef = ref();
        
        // 强制重新渲染的key
        const tableKey = ref(0);

        // 搜索表单
        const searchForm = ref<any>({
            material_code: '',
            organization_id: ALL,
            department_id: ALL,
            category_id: ALL,
            material_name: '',
        });

        // 对话框相关
        const dialogFormVisible = ref<boolean>(false);
        const dialogAlarmVisible = ref<boolean>(false);
        const dialogConstantVisible = ref<boolean>(false);
        const dialogVisible = ref<boolean>(false);
        const dialogFormShowDel = ref<boolean>(false);
        const dialogImageUrl = ref<string>('');
        const fileList = ref<any[]>([]);

        // 库存转移对话框相关
        const dialogTransferInventoryVisible = ref<boolean>(false);
        const transferInventoryStep = ref<number>(1);
        const transferInventoryForm = ref({
            inventory_id: '',
            sk: '',
            organization_id: '',
            department_id: '',
            category_id: '',
            location: '',
            comment: '',
            quantity: 0,
            storage_date: '',
            expiration_date: '',
            images: '',
            organization_name: '',
            department_name: '',
            category_name: '',
            material_code: '',
            material_name: '',
            jancode: '',
            dest_department_id: '',
            dest_department_name: '',
            transfer_memo:'',
            transfer_quantity: 0,
            dest_inventory_id: '',
            dest_sk: '',
            is_transfer: false,
            is_create_new_record: false,
            transfer_operator: '',
        });
        const targetInventoryList = ref<InventoryInfo[]>([]);
        const targetInventoryListLoading = ref<boolean>(false);
        const selectedTargetInventoryId = ref<string>('');
        const selectedTargetInventory = ref<{inventory_id: string, sk: string}>({
            inventory_id: '',
            sk: ''
        });

        // 定数设置维度类型
        const constantSettingLevel = ref<'organization' | 'department'>('organization');

        // 错误处理对象
        const errors = ref<{ jancode?: string }>({});

        // 表单数据
        const form = ref<InventoryInfo>({
            add_update: Operation.unknow,
            inventory_id: '',
            sk: '',
            material_code: '',
            organization_id: '',
            department_id: '',
            category_id: '',
            location: '',
            comment: '',
            quantity: 0,
            storage_date: '',
            expiration_date: '',
            images: '',
            organization_name: '',
            department_name: '',
            category_name: '',
            material_name: '',
            jancode: '',
        });

        const alarmForm = ref<AlarmInfo>({
            inventory_id: '',
            sk: '',
            organization_id: '',
            responsible_person: '',
            alarm_setting: [],
            quantity: 0,
            is_actived: '0',
            add_update: Operation.unknow,
        });

        const constantForm = ref<ConstantInfo>({
            inventory_id: '',
            sk: '',
            organization_id: '',
            responsible_person: '',
            constant_value: 0,
            add_update: Operation.unknow,
        });

        // 生命周期
        onMounted(() => {
            if (!loginUserRole) {
                router.push(`/${ROUTES.LOGIN}`);
                return;
            }
            getInventoryList();
        });

        // 添加监听器来清除JANコード相关的错误
        watch(() => form.value.jancode, (newVal) => {
            if (newVal && errors.value.jancode === 'JANコードを入力してください') {
                errors.value.jancode = '';
            }
        });

        watch(() => form.value.organization_id, (newVal) => {
            if (newVal && errors.value.jancode === '組織を選択してください') {
                errors.value.jancode = '';
            }
        });

        // 监听对话框打开状态，清除验证错误
        watch(() => dialogFormVisible.value, (newVal, oldVal) => {
            // 如果对话框正在关闭（从true变为false）
            if (oldVal === true && newVal === false) {
                // 清除JANコード相关的错误
                errors.value.jancode = '';
            }
        });

        // 方法
        const getInventoryList = async () => {
            try {
                loading.value = true;

                const params: SearchRequest = {
                    material_code: searchForm.value.material_code,
                    organization_id: searchForm.value.organization_id === ALL ? '' : searchForm.value.organization_id,
                    department_id: searchForm.value.department_id === ALL ? '' : searchForm.value.department_id,
                    category_id: searchForm.value.category_id === ALL ? '' : searchForm.value.category_id,
                    material_name: searchForm.value.material_name,
                };

                const res: AxiosResponse<ResponseData<InventoryInfo[]> & { level?: number }> = await axios.get('/api/inventories2/searchLevel1', { params });
                
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    searchDepartmentId.value = searchForm.value.department_id === ALL ? null : searchForm.value.department_id;
                    currentLevel.value = res.data.level || 1; // 从API获取层级信息

                    const { total, data, hasMoreData } = res.data;

                    totalRecords.value = total || 0;
                    
                    // 完全重置所有展开状态和详情数据
                    const processedData = (data || []).map(item => ({
                        ...item,
                        isExpand: false,
                        details: [] // 清空所有详情数据
                    }));
                    
                    allData.value = processedData;
                    
                    // 计算分页
                    calculatePagination();

                    // 如果有更多数据，显示提示信息
                    if (hasMoreData) {
                        ElMessage({
                            message: '検索結果が1000件を超えています。より詳細な検索条件を設定してください。',
                            type: 'warning',
                            duration: 5000,
                            showClose: true
                        });
                    }
                } else {
                    ElMessage.error(getErrorMessage(PageType.INVENTORY, res.data.code));
                }
            } catch (error) {
                ElMessage.error(getErrorMessage(PageType.INVENTORY));
            } finally {
                loading.value = false;
            }
        };

        // 前端分页和排序方法
        const handleSizeChange = (size: number) => {
            pageSize.value = size;
            calculatePagination();
        };
        
        const handleCurrentChange = (page: number) => {
            currentPage.value = page;
            calculatePagination();
        };

        // 处理排序
        const handleSort = (sortInfo: { field: string, order: 'asc' | 'desc' }) => {
            sortField.value = sortInfo.field;
            sortOrder.value = sortInfo.order;
            calculatePagination();
        };

        const handleSort2 = (field: string) => {
            if (sortField.value === field) {
                // 如果点击的是当前排序字段，切换排序方向
                sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
            } else {
                // 如果点击的是新字段，使用该字段的默认排序方向
                sortField.value = field;
                sortOrder.value = sortConfig[field as keyof typeof sortConfig]?.defaultOrder || 'asc';
            }
            calculatePagination();
        };

        // 获取排序图标
        const getSortIcon = (field: string) => {
            if (sortField.value !== field) {
                return 'unfold_more'; // 未排序状态
            }
            return sortOrder.value === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
        };

        // 获取排序字段的标签
        const getSortLabel = (field: string, isMobile: boolean = false) => {
            const config = sortConfig[field as keyof typeof sortConfig];
            return config ? (isMobile ? config.mobileLabel : config.label) : field;
        };

        // 比较函数工厂
        const createComparator = (field: string, order: 'asc' | 'desc') => {
            const config = sortConfig[field as keyof typeof sortConfig];
            const fieldType = config?.type || 'string';
            
            return (a: InventoryInfo, b: InventoryInfo) => {
                let aValue: any = a[field as keyof InventoryInfo];
                let bValue: any = b[field as keyof InventoryInfo];
                
                // 根据字段类型进行特殊处理
                switch (fieldType) {
                    case 'number':
                        aValue = Number(aValue) || 0;
                        bValue = Number(bValue) || 0;
                        break;
                    case 'date':
                        aValue = aValue ? new Date(aValue).getTime() : 0;
                        bValue = bValue ? new Date(bValue).getTime() : 0;
                        break;
                    case 'string':
                        if (typeof aValue === 'string' && typeof bValue === 'string') {
                            aValue = aValue.toLowerCase();
                            bValue = bValue.toLowerCase();
                        }
                        break;
                }
                
                // 处理空值
                if (aValue === null || aValue === undefined) aValue = '';
                if (bValue === null || bValue === undefined) bValue = '';
                
                let comparison = 0;
                if (aValue < bValue) comparison = -1;
                if (aValue > bValue) comparison = 1;
                
                // 根据排序方向返回结果
                return order === 'asc' ? comparison : -comparison;
            };
        };

        // 前端分页计算（支持多项目排序）
        const calculatePagination = () => {
            // 使用比较函数工厂创建排序函数
            const comparator = createComparator(sortField.value, sortOrder.value);
            
            // 对数据进行排序
            const sortedData = [...allData.value].sort(comparator);
            
            // 计算分页
            const startIndex = (currentPage.value - 1) * pageSize.value;
            const endIndex = startIndex + pageSize.value;
            
            tableData.value = sortedData.slice(startIndex, endIndex);
            totalRecords.value = allData.value.length;
        };

        // 事件处理
        const handleSearch = () => {
            console.log('handleSearch');

            // 收起所有展开的行
            collapseAllExpandedRows();
            
            currentPage.value = 1; // 重置到第一页
            
            getInventoryList();
        };


        /**
         * 添加库存信息
         */
        const handleAdd = () => {
            dialogFormShowDel.value = false;
            // 先重置再打开，避免第二次带出上次的有效期
            form.value = {
                add_update: Operation.add,
                inventory_id: '',
                sk: '',
                material_code: '',
                organization_id: loginUserRole === ROLES.SYSTEM ? '' : (loginUserOrganizationId ?? ''),
                department_id: loginUserRole === ROLES.SYSTEM || (loginUserRole === ROLES.ADMIN && loginUserRoleIsHead) ? '' : (loginUserDepartmentId ?? ''),
                category_id: '',
                location: '',
                comment: '',
                quantity: 0,
                storage_date: new Date(Date.now()).toISOString(),
                expiration_date: '',
                images: '',
                organization_name: loginUserRole === ROLES.SYSTEM ? '' : (loginUserOrganizationName ?? ''),
                department_name: loginUserRole === ROLES.SYSTEM || (loginUserRole === ROLES.ADMIN && loginUserRoleIsHead) ? '' : (loginUserDepartmentName ?? ''),
                category_name: '',
                material_name: '',
                jancode: '',
            };
            // 清空文件列表
            fileList.value = [];
            // 清空错误信息
            errors.value = {};
            dialogFormVisible.value = true;
        };

        /**
         * 导出库存信息
         */
        const handleExport = async () => {
            ElMessageBox.confirm('在庫一覧をエクスポートしますか？', 'CSVエクスポート', {
                confirmButtonText: 'エクスポート',
                cancelButtonText: 'キャンセル',
                type: 'warning',
            }).then(async () => {
                try {
                    loading.value = true;
                    
                    // 直接调用导出 API，不传递任何参数
                    const res = await axios.post('/api/inventories2/export', {}, {
                        responseType: 'blob' // 设置响应类型为 blob 以处理文件下载
                    });

                    if (res.status === ResponseStatus.SUCCESS) {
                        // 创建下载链接
                        const blob = new Blob([res.data], { 
                            type: 'text/csv;charset=utf-8;' 
                        });
                        const url = window.URL.createObjectURL(blob);
                        
                        // 创建临时下载链接
                        const link = document.createElement('a');
                        link.href = url;
                        
                        // 生成文件名（包含当前时间戳）
                        const now = new Date();
                        const timestamp = now.getFullYear() + 
                                       String(now.getMonth() + 1).padStart(2, '0') + 
                                       String(now.getDate()).padStart(2, '0') + '_' +
                                       String(now.getHours()).padStart(2, '0') + 
                                       String(now.getMinutes()).padStart(2, '0') + 
                                       String(now.getSeconds()).padStart(2, '0');
                        link.download = `在庫一覧_${timestamp}.csv`;
                        
                        // 触发下载
                        document.body.appendChild(link);
                        link.click();
                        
                        // 清理
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                        
                        ElMessage.success('CSVファイルが正常にエクスポートされました。');
                    } else {
                        ElMessage.error('エクスポートに失敗しました。');
                    }
                } catch (error) {
                    console.error('导出失败:', error);
                    ElMessage.error('エクスポートに失敗しました。');
                } finally {
                    loading.value = false;
                }
            }).catch(() => {
                // 用户取消导出
                console.log('用户取消导出');
            });
        };

        /**
         * 导入库存信息
         */
        const handleImport = () => {
            // 创建文件输入元素
            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.accept = '.csv';
            fileInput.style.display = 'none';
            
            // 监听文件选择事件
            fileInput.addEventListener('change', async (event) => {
                const target = event.target as HTMLInputElement;
                const file = target.files?.[0];
                
                if (!file) {
                    ElMessage.warning('ファイルを選択してください。');
                    return;
                }
                
                // 检查文件类型
                if (!file.name.toLowerCase().endsWith('.csv')) {
                    ElMessage.error('CSVファイルのみ対応しています。');
                    return;
                }
                
                try {
                    loading.value = true;
                    
                    // 创建 FormData 对象
                    const formData = new FormData();
                    formData.append('file', file);
                    
                    // 调用导入 API
                    const res = await axios.post('/api/inventories2/import', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                    
                    if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                        const result = res.data;
                        
                        // 显示导入结果
                        if (result.failedRecords && result.failedRecords.length > 0) {
                            // 有失败的记录，显示详细信息
                            ElMessageBox.alert(
                                `インポート結果：\n` +
                                `総数：${result.totalCount}件\n` +
                                `成功：${result.validCount}件\n` +
                                `失敗：${result.invalidCount}件\n\n` +
                                `失敗したレコード：\n${result.failedRecords.map((record: {id: string, error: string}) => 
                                    `ID: ${record.id} - ${record.error}`
                                ).join('\n')}`,
                                'インポート結果',
                                {
                                    confirmButtonText: 'OK',
                                    type: 'warning'
                                }
                            );
                        } else {
                            // 全部成功
                            ElMessage.success(`インポートが完了しました。\n総数：${result.totalCount}件、成功：${result.validCount}件`);
                        }
                        
                        // 刷新数据列表
                        await getInventoryList();
                    } else {
                        ElMessage.error(getErrorMessage(PageType.INVENTORY, res.data.code) || 'インポートに失敗しました。');
                    }
                } catch (error) {
                    console.error('导入失败:', error);
                    ElMessage.error('インポートに失敗しました。');
                } finally {
                    loading.value = false;
                    // 清理文件输入元素
                    document.body.removeChild(fileInput);
                }
            });
            
            // 触发文件选择对话框
            document.body.appendChild(fileInput);
            fileInput.click();
        };

        /**
         * 转移库存信息
         * @param item 
         */
        const handleTransferInventory = (item: InventoryInfo) => {
            transferInventoryForm.value = {
                ...transferInventoryForm.value,
                inventory_id: item.inventory_id,
                sk: item.sk,
                storage_date: item.storage_date ?? '',
                expiration_date: item.expiration_date ?? '',
                organization_id: item.organization_id ?? '',
                organization_name: item.organization_name ?? '',
                department_id: item.department_id ?? '',
                department_name: item.department_name ?? '',
                material_code: item.material_code ?? '',
                material_name: item.material_name ?? '',
                comment: item.comment ?? '',
                location: item.location ?? '',
                quantity: item.quantity ?? 0,
                dest_department_id: '',
                dest_department_name: '',
                transfer_memo: '',
                transfer_quantity: 0,
                is_transfer: false,
                is_create_new_record: false,
                transfer_operator: '',
            };

            dialogTransferInventoryVisible.value = true;
            transferInventoryStep.value = 1;
            selectedTargetInventoryId.value = ''; // Reset selection
            selectedTargetInventory.value = { inventory_id: '', sk: '' }; // Reset selection object
        };


        /**
         * 更新库存信息
         * @param item 
         */
        const handleEditInventory = (item: InventoryInfo) => {
            dialogFormVisible.value = true;
            dialogFormShowDel.value = false;
            nextTick(async () => {
                form.value = {
                    add_update: Operation.update,
                    inventory_id: item.inventory_id,
                    sk: item.sk,
                    material_code: item.material_code,
                    organization_id: item.organization_id,
                    department_id: item.department_id,
                    category_id: item.category_id,
                    location: item.location,
                    quantity: item.quantity,
                    storage_date: item.storage_date,
                    expiration_date: item.expiration_date,
                    comment: item.comment,
                    images: item.images,
                    organization_name: item.organization_name,
                    department_name: item.department_name,
                    category_name: item.category_name,
                    material_name: item.material_name,
                    jancode: item.jancode,
                };
                if (item.images) {
                    let bufImages = item.images.split(',');
                    fileList.value = await getImages(bufImages);
                } else {
                    fileList.value = [];
                }
            });
        };


        /**
         * 删除库存信息
         * @param item 
         */
        const handleDeleteInventory = (item: InventoryInfo) => {
            console.log('handleDeleteInventory 被调用，item:', item);
            ElMessageBox.confirm('削除しますか？', '削除', {
                confirmButtonText: '削除',
                cancelButtonText: 'キャンセル',
                type: 'warning',
            }).then(() => {
                console.log('用户确认删除，调用 deleteInventory');
                deleteInventory(item);
            }).catch(() => {
                console.log('用户取消删除');
                // 用户取消删除
            });
        };


        /**
         * 设置报警信息
         * @param item 
         */
        const handleAlarmInventory = (item: InventoryInfo) => {
            alarmForm.value = {
                inventory_id: item.inventory_id,
                sk: item.sk,
                organization_id: item.organization_id,
                responsible_person: item.responsible_person ?? '',
                alarm_setting: [],
                quantity: 0,
                is_actived: '0',
                add_update: Operation.unknow,
            };

            getAlarmSetting(alarmForm.value).then(() => {
                dialogAlarmVisible.value = true;
                if (alarmForm.value.alarm_setting && alarmForm.value.alarm_setting.length > 0) {
                    alarmForm.value.add_update = Operation.update;
                } else {
                    alarmForm.value.add_update = Operation.add;
                    // 新增时，默认打开有效开关
                    alarmForm.value.is_actived = '1';
                }
            });
        };

        /**
         * 设置定数信息
         * @param item 
         */
        const handleConstantInventory = (item: InventoryInfo) => {

            if (item.sk.startsWith('SUM#')) {
                constantSettingLevel.value = 'organization';
            } else {
                constantSettingLevel.value = 'department';
            }
            
            constantForm.value = {
                inventory_id: item.inventory_id,
                sk: item.sk,
                organization_id: item.organization_id,
                responsible_person: item.responsible_person ?? '',
                constant_value: 0,
                add_update: Operation.unknow,
            };

            getConstantSetting(constantForm.value).then(() => {
                dialogConstantVisible.value = true;
                if (constantForm.value.constant_value > 0) {
                    constantForm.value.add_update = Operation.update;
                } else {
                    constantForm.value.add_update = Operation.add;
                }
            });
        };

        /**
         * 展开事件
         * @param item 
         */
        const handleExpand = async (item: InventoryInfo) => {
            // 如果还没有详情数据，则获取
            if (!item.details || item.details.length === 0) {
                try {
                    loading.value = true;
                    
                    let params: any;
                    let apiUrl: string;
                    
                    if (currentLevel.value === 1) {
                        // 组织级别：获取部门详情
                        apiUrl = '/api/inventories2/searchLevel2';
                        params = {
                            organization_id: item.organization_id,
                            material_code: item.material_code,
                            page: 1,
                            limit: 100
                        };
                    } else if (currentLevel.value === 2) {
                        // 部门级别：获取详细库存信息
                        apiUrl = '/api/inventories2/searchLevel3';
                        params = {
                            department_id: item.department_id,
                            material_code: item.material_code,
                            page: 1,
                            limit: 100
                        };
                    } else {
                        // 层级未知，直接返回
                        return;
                    }
                    
                    const res: AxiosResponse<ResponseData<InventoryInfo[]>> = await axios.get(apiUrl, { params });
                    
                    if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                        // 更新当前项的details数据
                        const index = tableData.value.findIndex(data => data.inventory_id === item.inventory_id);
                        if (index !== -1) {
                            // 处理详情数据，确保每个部门项也有正确的展开状态
                            const processedDetails = (res.data.data || []).map(detailItem => ({
                                ...detailItem,
                                isExpand: false,
                                details: []
                            }));
                            
                            tableData.value[index].details = processedDetails;
                            // 强制更新响应式数据
                            tableData.value = [...tableData.value];
                        }
                    } else {
                        ElMessage.error('詳細データの取得に失敗しました。');
                    }
                } catch (error) {
                    ElMessage.error('詳細データの取得に失敗しました。');
                } finally {
                    loading.value = false;
                }
            }
        };

        /**
         * 处理嵌套展开（从组织级别展开部门级别时，再展开详细级别）
         * @param item 
         */
        const handleNestedExpand = async (item: InventoryInfo) => {
            // 检查必要参数是否存在
            if (!item.department_id) {
                ElMessage.error('部門IDが不足しています。');
                return;
            }
            
            if (!item.material_code) {
                ElMessage.error('材料コードが不足しています。');
                return;
            }
            
            // 如果还没有详情数据，则获取
            if (!item.details || item.details.length === 0) {
                try {
                    loading.value = true;
                    
                    const params = {
                        department_id: item.department_id,
                        material_code: item.material_code,
                        page: 1,
                        limit: 100
                    };
                    
                    const apiUrl = '/api/inventories2/searchLevel3';
                    
                    const res: AxiosResponse<ResponseData<InventoryInfo[]>> = await axios.get(apiUrl, { params });
                    
                    if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                        // 在组织级别的数据中查找对应的部门级别数据
                        let found = false;
                        for (let i = 0; i < tableData.value.length; i++) {
                            const orgItem = tableData.value[i];
                            if (orgItem.details && orgItem.details.length > 0) {
                                const deptIndex = orgItem.details.findIndex(deptItem => deptItem.inventory_id === item.inventory_id);
                                if (deptIndex !== -1) {
                                    // 找到对应的部门级别数据，更新其details
                                    const processedDetails = (res.data.data || []).map(detailItem => ({
                                        ...detailItem,
                                        isExpand: false,
                                        details: []
                                    }));
                                    
                                    // 确保details存在
                                    const details = tableData.value[i].details;
                                    if (details && details[deptIndex]) {
                                        const deptItem = details[deptIndex];
                                        if (deptItem) {
                                            deptItem.details = processedDetails;
                                            // 强制更新响应式数据
                                            tableData.value = [...tableData.value];
                                            found = true;
                                        }
                                    }
                                    break;
                                }
                            }
                        }
                    } else {
                        ElMessage.error(`詳細データの取得に失敗しました: ${res.data.message || '未知错误'}`);
                    }
                } catch (error) {
                    ElMessage.error('詳細データの取得に失敗しました。');
                } finally {
                    loading.value = false;
                }
            }
        };

        /**
         * 收起事件
         * @param item 
         */
        const handleCollapse = (item: InventoryInfo) => {
            // 设置当前项为收起状态
            item.isExpand = false;
            
            // 递归收起所有子项的展开状态
            const collapseAllChildren = (details: InventoryInfo[]) => {
                if (details && details.length > 0) {
                    details.forEach(detailItem => {
                        detailItem.isExpand = false;
                        // 递归处理更深层级的子项
                        if (detailItem.details && detailItem.details.length > 0) {
                            collapseAllChildren(detailItem.details);
                        }
                    });
                }
            };
            
            // 收起所有子项
            if (item.details) {
                collapseAllChildren(item.details);
            }
            
            // 强制更新响应式数据
            tableData.value = [...tableData.value];
        };

        /**
         * 收起所有展开的行
         */
        const collapseAllExpandedRows = () => {
            // 强制重新渲染表格来收起所有展开的行
            tableKey.value += 1;
            
            // 重置所有数据的展开状态
            const resetExpandState = (items: InventoryInfo[]) => {
                items.forEach(item => {
                    item.isExpand = false;
                    if (item.details && item.details.length > 0) {
                        resetExpandState(item.details);
                    }
                });
            };
            
            resetExpandState(tableData.value);
            
            // 强制更新响应式数据
            tableData.value = [...tableData.value];
            
            console.log('已重置所有展开状态，表格key:', tableKey.value);
        };


        const handleQuantityUpdate = async ({ item, newQuantity }: { item: InventoryInfo, newQuantity: number }) => {
            try {
                loading.value = true;

                const res: AxiosResponse<ResponseData<InventoryInfo>> = await axios.put(`/api/inventories2/${item.inventory_id}`, {
                    sk: item.sk,
                    quantity: newQuantity,
                });

                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    ElMessage.success('在庫データの数量が正常に更新されました。');
                    
                    // Refresh the inventory list to display newest state
                    collapseAllExpandedRows();
                    await getInventoryList();
                } else {
                    ElMessage.error(getErrorMessage(PageType.INVENTORY, res.data.code));
                }
            } catch (error) {
                ElMessage.error(getErrorMessage(PageType.INVENTORY));
            } finally {
                loading.value = false;
            }
        };


        /**
         * 表单提交事件
         */
        const handleFormSubmit = async () => {
            try {
                loading.value = true;

                // 图片上传处理
                let images = [];
                if (fileList.value.length > 0) {
                    const imageInfos = await uploadImages(fileList.value);
                    if (imageInfos) {
                        for (const imageInfo of imageInfos) {
                            images.push(imageInfo.id);
                        }
                    } else {
                        ElMessage.error('画像アップロード失敗しました。');
                        return;
                    }
                }

                // 设置表单数据
                form.value.material_name = form.value.material_name || '';
                form.value.organization_name = form.value.organization_name || '';
                form.value.department_name = form.value.department_name || '';
                form.value.category_name = form.value.category_name || '';
                form.value.images = images.join(',');

                let res: AxiosResponse<ResponseData<InventoryInfo>>;
                
                // 根据操作类型调用不同的API
                if (form.value.add_update === Operation.add) {
                    // 新增操作
                    res = await axios.post('/api/inventories2', form.value);
                    if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                        ElMessage.success('在庫データが正常に追加されました。');
                    }
                } else if (form.value.add_update === Operation.update) {
                    // 更新操作
                    res = await axios.put(`/api/inventories2/${form.value.inventory_id}`, form.value);
                    if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                        ElMessage.success('在庫データが正常に更新されました。');
                    }
                } else {
                    ElMessage.error('無効な操作タイプです。');
                    return;
                }
                
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    dialogFormVisible.value = false;

                    // 收起所有展开的行
                    collapseAllExpandedRows();
                    await getInventoryList();
                } else {
                    ElMessage.error(getErrorMessage(PageType.INVENTORY, res.data.code));
                }
            } catch (error) {
                ElMessage.error(getErrorMessage(PageType.INVENTORY));
            } finally {
                loading.value = false;
            }
        };



        const getTargetInventoryList = async () => {
            targetInventoryListLoading.value = true;
            const res: AxiosResponse<ResponseData<InventoryInfo[]>> = await axios.get(`/api/inventories2/searchLevel3`, {
                params: {
                    organization_id: transferInventoryForm.value.organization_id,
                    department_id: transferInventoryForm.value.dest_department_id, // Use destination department for target inventory
                    material_code: transferInventoryForm.value.material_code,
                    storage_date: transferInventoryForm.value.storage_date,
                    expiration_date: transferInventoryForm.value.expiration_date,
                    page: 1,
                    limit: 1000 // Get more data for client-side pagination
                }
            });

            if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                targetInventoryList.value = res.data.data || [];
            } else {
                ElMessage.error(getErrorMessage(PageType.INVENTORY, res.data.code));
            }
            targetInventoryListLoading.value = false;
        }


        /**
         * 払い出す　目標在庫ありの処理
         */
        const handleTransferInventorySubmit = () => {
            console.log('handleTransferInventorySubmit', transferInventoryForm.value);
            InventoryTransfer();
        };


        /**
         * 払い出す　目標在庫なしの処理
         * @param selection 
         */
        const handleCreateNewRecord = async () => {
            console.log('handleCreateNewRecord', transferInventoryForm.value);
            InventoryTransfer();
        };


        const InventoryTransfer = async () => {
            try {
                loading.value = true;
                const postData = {
                    ...transferInventoryForm.value,
                };

                const res: AxiosResponse<ResponseData<AlarmInfo[]>> = await axios.post(`/api/inventories2/transfer`, postData);
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    ElMessage.success('在庫データが正常に移動されました。');
                    dialogTransferInventoryVisible.value = false;
                    collapseAllExpandedRows();

                    if ((postData.quantity - postData.transfer_quantity) === 0) {
                        ElMessageBox.confirm('在庫数 0 を削除しますか？', '確認', {
                            confirmButtonText: 'はい',
                            cancelButtonText: 'いいえ',
                            type: 'warning',
                        }).then(() => {
                            deleteInventory({ inventory_id: postData.inventory_id, sk: postData.sk } as InventoryInfo);
                        }).catch(() => {
                            // 用户取消删除
                        });
                    }
                } else {
                    ElMessage.error(getErrorMessage(PageType.INVENTORY, res.data.code));
                }
            } catch (error) {
                ElMessage.error(getErrorMessage(PageType.INVENTORY));
            } finally {
                await getInventoryList();
            }
        };



        /**
         * 选择　库存转移（有目标部门记录）
         * @param selection 
         */
        const handleTargetInventorySelect = (selection: {inventory_id: string, sk: string}) => {
            selectedTargetInventoryId.value = selection.inventory_id;
            selectedTargetInventory.value = selection;
        };

        /**
         * 报警设置提交事件
         * 注意：验证已经在对话框内部完成，这里只负责保存数据
         */
        const handleAlarmSubmit = () => {
            try {
                saveAlarmSetting(alarmForm.value);
                dialogAlarmVisible.value = false;
            } catch (error) {
                ElMessage.error('必須項目を入力してください');
            }
        };

        
        const saveAlarmSetting = async (row: AlarmInfo) => {
            try {
                loading.value = true;
                const dataToSend = {
                    ...alarmForm.value,
                    alarm_setting: alarmForm.value.alarm_setting.join(','), // 将数组转换为逗号分隔的字符串
                };

                const res: AxiosResponse<ResponseData<AlarmInfo[]>> = await axios.post(`/api/reminder-settings`, dataToSend);
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    // 收起所有展开的行
                    collapseAllExpandedRows();
                    await getInventoryList();
                } else {
                    ElMessage.error(getErrorMessage(PageType.INVENTORY, res.data.code));
                }
            } catch (error) {
                ElMessage.error(getErrorMessage(PageType.INVENTORY));
            } finally {
                loading.value = false;
            }
        };

        const handleConstantSubmit = () => {
            // 定数设置提交逻辑
            saveConstantSetting(constantForm.value);
        };

        /**
         * 保存常数设置
         * @param row 
         */
        const saveConstantSetting = async (row: ConstantInfo) => {
            try {
                loading.value = true;
                const dataToSend = {
                    ...constantForm.value,
                    responsible_person: constantForm.value.responsible_person || '', // 暂时设置为空字符串
                };

                const res: AxiosResponse<ResponseData<ConstantInfo[]>> = await axios.post(`/api/constant-settings`, dataToSend);
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    ElMessage.success('定数設定が正常に保存されました。');
                    dialogConstantVisible.value = false;
                    // 收起所有展开的行
                    collapseAllExpandedRows();
                    await getInventoryList();
                } else {
                    ElMessage.error(getErrorMessage(PageType.INVENTORY, res.data.code));
                }
            } catch (error) {
                ElMessage.error(getErrorMessage(PageType.INVENTORY));
            } finally {
                loading.value = false;
            }
        };




        const handleDeleteReminder = async () => {
            try {
                await ElMessageBox.confirm(
                    'このアラート設定を削除しますか？',
                    '確認',
                    {
                        confirmButtonText: '削除',
                        cancelButtonText: 'キャンセル',
                        type: 'warning',
                    }
                );

                // Call API to delete reminder setting using the inventory_id and sk
                const res: AxiosResponse<ResponseData<any>> = await axios.delete(`/api/reminder-settings/${alarmForm.value.inventory_id}`, {
                    data: { sk: alarmForm.value.sk }
                });
                
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    ElMessage.success('アラート設定を削除しました');
                    dialogAlarmVisible.value = false;
                    // Refresh the inventory list
                    collapseAllExpandedRows();
                    await getInventoryList();
                } else {
                    ElMessage.error(getErrorMessage(PageType.INVENTORY, res.data.code) || 'アラート設定の削除に失敗しました');
                }
            } catch (error: any) {
                if (error !== 'cancel') {
                    console.error('Failed to delete reminder setting:', error);
                    ElMessage.error('アラート設定の削除に失敗しました');
                }
            }
        };

        const handleImageChange = (file: UploadFile) => {
            if (fileList.value.length >= 4) {
                ElMessage.warning('最大3枚までアップロードできます。');
                return false;
            }
            
            fileList.value.push(file);
        };

        const handleImageRemove = (file: UploadFile) => {
            const index = fileList.value.findIndex(item => item.name === file.name);
            if (index !== -1) {
                fileList.value.splice(index, 1);
                // 触发视图更新
                fileList.value = [...fileList.value];
            }
        };

        const handleImagePreview = (file: UploadFile) => {
            dialogImageUrl.value = file.url || '';
            dialogVisible.value = true;
        };

        const handleScanStart = () => {
            // 对于条码扫描，只验证组织ID
            if (!form.value.organization_id) {
                errors.value.jancode = '組織を選択してください';
                return;
            }
            
            // 清除错误
            errors.value.jancode = '';
            
            // 扫描器功能现在由 InventoryDialogs 组件内部处理
            // 这里不需要额外的逻辑，扫描器会在对话框中自动启动
        };

        /**
         * 处理条码扫描成功
         * @param jancode 
         */
        const handleScanSuccess = async (jancode: string) => {
            try {
                getJanCodeInfo(jancode).then(materialInfoResult => {
                    // 检查结果是否为数组并使用第一个项目
                    const materialInfo = Array.isArray(materialInfoResult) && materialInfoResult.length > 0 
                        ? materialInfoResult[0] 
                        : materialInfoResult;
                        
                    if (materialInfo && Object.keys(materialInfo).length > 0) {
                        // 更新表单数据
                        form.value.material_code = materialInfo.material_code;
                        form.value.material_name = materialInfo.name;
                        form.value.category_id = materialInfo.category_id;
                        form.value.jancode = materialInfo.jancode;
                    } else {
                        errors.value.jancode = 'アイテムマスタにありません。アイテムマスタに登録してください。';
                    }
                });
            } catch (error) {
                ElMessage.error('JANコードの取得に失敗しました。');
            }
        };

        /**
         * 更新组织ID
         * @param newOrganizationId 
         */
        const updateOrganizationId = (newOrganizationId: string) => {
            form.value.organization_id = newOrganizationId;
        };

        /**
         * 更新分类ID
         * @param newCategoryId 
         */
        const updateCategoryId = (newCategoryId: string) => {
            form.value.category_id = newCategoryId;
        };

        /**
         * 更新有效期
         * @param newExpirationDay 
         */
        const updateExpirationDate = (newExpirationDay: number) => {
            if (form.value.storage_date) {
                // 计算新的有效期日期
                const storageDate = new Date(form.value.storage_date);
                const expirationDate = new Date(storageDate);
                expirationDate.setDate(storageDate.getDate() + newExpirationDay);
                
                // 更新表单中的有效期
                form.value.expiration_date = expirationDate.toISOString().split('T')[0];
            }
        };

        /**
         * 扫描 JAN 码
         * @param jancode 
         */
        const handleJancodeSearch = async (jancode: string) => {
            // 检查 JAN 码是否为空
            if (!form.value.jancode || form.value.jancode === '') {
                errors.value.jancode = 'JANコードを入力してください';
                return;
            }
            
            // 检查组织是否已选择
            if (!form.value.organization_id) {
                errors.value.jancode = '組織を選択してください';
                return;
            }
            
            // 清除错误
            errors.value.jancode = '';
            
            getJanCodeInfo(jancode).then(materialInfoResult => {
                const materialInfo = Array.isArray(materialInfoResult) && materialInfoResult.length > 0 
                    ? materialInfoResult[0] 
                    : materialInfoResult;
                    
                if (materialInfo && Object.keys(materialInfo).length > 0) {
                    // 更新表单数据
                    form.value.material_code = materialInfo.material_code;
                    form.value.material_name = materialInfo.name;
                    form.value.category_id = materialInfo.category_id;
                    const calculatedDate = resolveExpirationDateFromDay(
                        materialInfo.expiration_day,
                        form.value.storage_date
                    );
                    if (calculatedDate) {
                        form.value.expiration_date = calculatedDate;
                    }
                } else {
                    errors.value.jancode = 'アイテムマスタにありません。アイテムマスタに登録してください。';
                }
            });
        };

        /**
         * 获取 JAN 码信息
         * @param jancode 
         */
        const getJanCodeInfo = async (jancode: string) => {
            try {
                const params = {
                    jancode: jancode,
                    organization_id: form.value.organization_id,
                }

                const res: AxiosResponse<ResponseData<any>> = await axios.get(`/api/materials/jancode`, { params });
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    return res.data.data;
                }
                return null;
            } catch (error) {
                return null;
            }
        };


        /**
         * 表单更新事件
         * @param updatedForm 
         * 同步表单数据：使用 Object.assign(form.value, updatedForm) 
         * 将子组件传递过来的更新数据合并到父组件的 form.value 中
         * 保持数据一致性：确保父组件和子组件之间的表单数据保持同步
         * 
         * 用户在 InventoryDialogs 组件中修改表单字段（如输入JAN码、选择组织、部门等） 向父组件发送更新事件
         * 父组件接收到更新事件后，调用 handleFormUpdate 方法，将更新数据合并到 form.value 中
         * 
         */
        const handleFormUpdate = (updatedForm: InventoryInfo) => {
            Object.assign(form.value, updatedForm);
        };


        /**
         * 报警表单更新事件
         * @param updatedAlarmForm 
         */
        const handleAlarmFormUpdate = (updatedAlarmForm: AlarmInfo) => {
            Object.assign(alarmForm.value, updatedAlarmForm);
        };

        /**
         * 定数表单更新事件
         * @param updatedConstantForm 
         */ 
        const handleConstantFormUpdate = (updatedConstantForm: ConstantInfo) => {
            Object.assign(constantForm.value, updatedConstantForm);
        };

        /**
         * 払い出し表单更新事件
         * @param updatedTransferInventoryForm 
         */
        const handleTransferInventoryFormUpdate = (updatedTransferInventoryForm: InventoryInfo) => {
            Object.assign(transferInventoryForm.value, updatedTransferInventoryForm);
        };


        /**
         * 搜索表单更新事件
         * @param newSearchForm 
         */
        const handleSearchFormUpdate = (newSearchForm: any) => {
            searchForm.value = newSearchForm;
            // 移除自动搜索，只在用户点击搜索按钮时才执行搜索
        };

        const handleErrorsUpdate = (newErrors: any) => {
            errors.value = newErrors;
        };

        /**
         * 删除库存
         * @param row 
         */
        const deleteInventory = async (row: InventoryInfo) => {
            console.log('deleteInventory 被调用，row:', row);
            dialogFormVisible.value = false;
            try {
                loading.value = true;
                console.log('发送删除请求到:', `/api/inventories2/${row.inventory_id}`);
                const res: AxiosResponse<ResponseData> = await axios.delete(`/api/inventories2/${row.inventory_id}`, {
                    data: {
                        sk: row.sk
                    }
                });
                console.log('删除请求响应:', res);
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    ElMessage.success('在庫データが正常に削除されました。');
                    // 收起所有展开的行
                    collapseAllExpandedRows();
                    await getInventoryList();
                } else {
                    ElMessage.error(getErrorMessage(PageType.INVENTORY, res.data.code));
                }
            }
            catch (error) {
                console.error('删除请求失败:', error);
                ElMessage.error(getErrorMessage(PageType.INVENTORY));
            } finally {
                loading.value = false;
            }
        };

        /**
         * 获取报警设置
         * @param row 
         */
        const getAlarmSetting = async (row: AlarmInfo) => {
            try {
                loading.value = true;
                const res: AxiosResponse<ReminderSettingResponseData> = await axios.get(`/api/reminder-settings/${row.inventory_id}`);
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    alarmForm.value = {
                        ...alarmForm.value,
                        inventory_id: res.data.inventory_id || '',
                        responsible_person: res.data.user_id?.toString() || '',
                        alarm_setting: res.data.type?.split(',') || [], // 将字符串分割为数组
                        quantity: res.data.quantity || 0,
                        is_actived: res.data.is_actived?.toString() || '0',
                    };
                } else {
                    alarmForm.value = {
                        ...alarmForm.value,
                        is_actived: '1', // 新增时，默认打开有效开关
                        inventory_id: row.inventory_id,
                        alarm_setting: [], // 初始化为空数组
                    };
                }
            } catch (error) {
                ElMessage.error(getErrorMessage(PageType.INVENTORY));
            } finally {
                loading.value = false;
            }
        };

        /**
         * 获取定数设置
         * @param row 
         */
        const getConstantSetting = async (row: ConstantInfo) => {
            try {
                loading.value = true;
                const res: AxiosResponse<ResponseData<ConstantInfo>> = await axios.get(`/api/constant-settings/${row.inventory_id}`, {
                    params: { sk: row.sk }
                });
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    constantForm.value = {
                        ...constantForm.value,
                        inventory_id: res.data.data?.inventory_id || row.inventory_id,
                        sk: res.data.data?.sk || row.sk,
                        organization_id: res.data.data?.organization_id || row.organization_id,
                        responsible_person: res.data.data?.responsible_person || '',
                        constant_value: res.data.data?.constant_value || 0,
                    };
                } else {
                    constantForm.value = {
                        ...constantForm.value,
                        inventory_id: row.inventory_id,
                        sk: row.sk,
                        organization_id: row.organization_id,
                        responsible_person: '',
                        constant_value: 0,
                    };
                }
            } catch (error) {
                ElMessage.error(getErrorMessage(PageType.INVENTORY));
            } finally {
                loading.value = false;
            }
        };

        /**
         * 获取图片
         * @param image_ids 
         */
        const getImages = async (image_ids: string[]) => {
            var fileLst: UploadFile[] = [];

            for (const image_id of image_ids) {
                const res = await axios.get(`/api/images/download/${image_id}`, { responseType: 'blob' });
                if (res.status === ResponseStatus.SUCCESS && res.data.code === undefined) {
                    const uid = parseInt(image_id);
                    fileLst.push({
                        url: URL.createObjectURL(res.data),
                        uid: uid,
                        name: image_id,
                        raw: res.data,
                        status: 'ready'
                    });
                } else {
                    ElMessage.error('画像情報の取得に失敗しました。');
                }
            }

            return fileLst;
        };

        /**
         * 图片上传函数
         * @param fileList 
         */
        const uploadImages = async (fileList: UploadFile[]) => {
            if (fileList.length > 0) {
                const formData = new FormData();
                
                // 统计所有文件的总大小
                let totalSize = 0;
                for (const tFile of fileList) {
                    if (tFile.raw) {
                        totalSize += tFile.raw.size;
                    }
                }
                
                // 检查总大小是否超过限制 (100KB)
                // const IMG_MAX_SIZE = 100 * 1024;
                if (totalSize > IMG_MAX_SIZE) {
                    ElMessage.error('ファイルが大きすぎます。');
                    return null;
                }
                
                for (const tFile of fileList) {
                    formData.append('files', tFile.raw || new Blob());
                    // 文件过大检查
                    if (tFile.raw && tFile.raw.size > IMG_MAX_SIZE) {
                        ElMessage.error('ファイルが大きすぎます。');
                        return null;
                    }
                }

                try {
                    const res: AxiosResponse<UploadImageResponseData> = await axios.post('/api/images/upload', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });

                    if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                        ElMessage.success('画像アップロード成功');
                        return res.data.images;
                    } else {
                        ElMessage.error('画像アップロード失敗');
                        return null;
                    }
                } catch (error) {
                    ElMessage.error('画像アップロード失敗');
                    return null;
                }
            } else {
                ElMessage.error('ファイルが無効です');
                return null;
            }
        };

        return {
            loginUserRole,
            loginUserRoleIsHead,
            loading,
            // isGrid,
            currentLevel,
            tableData,
            totalRecords,
            currentPage,
            pageSize,
            searchDepartmentId,
            organizationTableRef,
            departmentTableRef,
            detailTableRef,
            tableKey,
            searchForm,
            dialogFormVisible,
            dialogAlarmVisible,
            dialogConstantVisible,
            dialogVisible,
            dialogFormShowDel,
            dialogImageUrl,
            dialogTransferInventoryVisible,
            transferInventoryForm,
            targetInventoryList,
            selectedTargetInventoryId,
            selectedTargetInventory,
            fileList,
            constantSettingLevel,
            form,
            alarmForm,
            constantForm,
            handleSearch,
            handleAdd,
            handleExport,
            handleImport,
            // handleToggleGrid,
            handleEditInventory,
            handleDeleteInventory,
            handleTransferInventory,
            handleAlarmInventory,
            handleConstantInventory,
            handleExpand,
            handleNestedExpand,
            handleCollapse,
            handleQuantityUpdate,
            handleSizeChange,
            handleCurrentChange,
            handleSort,
            handleSort2,
            getSortIcon,
            getSortLabel,
            handleFormSubmit,
            handleAlarmSubmit,
            handleConstantSubmit,
            handleTransferInventorySubmit,
            getTargetInventoryList,
            handleCreateNewRecord,
            handleTargetInventorySelect,
            handleDeleteReminder,
            handleImageChange,
            handleImageRemove,
            handleImagePreview,
            handleScanStart,
            handleJancodeSearch,
            handleScanSuccess,
            updateOrganizationId,
            updateCategoryId,
            updateExpirationDate,
            handleFormUpdate,
            handleAlarmFormUpdate,
            handleConstantFormUpdate,
            handleTransferInventoryFormUpdate,
            handleSearchFormUpdate,
            handleErrorsUpdate,
            deleteInventory,
            getAlarmSetting,
            getConstantSetting,
            getImages,
            getJanCodeInfo,
            uploadImages,
            transferInventoryStep,
            targetInventoryListLoading,
            ROLES,
            errors,
            sortField,
        };
    },
});
</script> 

<style scoped>
/* 手机端排序按钮样式 */
.mobile-sort-btn {
    padding: 1px 4px;
    font-size: 11px;
    border-radius: 3px;
    transition: all 0.2s;
    white-space: nowrap;
    min-width: auto;
    height: 20px;
    line-height: 1;
    background-color: white;
    border: 1px solid #d1d5db;
}

.mobile-sort-btn:hover {
    background-color: #f8f9fa;
    color: #409eff;
    border-color: #409eff;
}

.mobile-sort-btn.active {
    background-color: #e6f7ff;
    color: #409eff;
    border-color: #409eff;
}
</style>